import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Lock, Mail, LogIn } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useAdminAuth } from '@/context/AdminAuthContext';

export default function AdminLogin() {
  const { user, loading, login } = useAdminAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const location = useLocation();

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="animate-pulse text-slate-400">Loading…</div></div>;
  if (user) return <Navigate to="/admin" replace state={{ from: location }} />;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error('Email and password required'); return; }
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success('Welcome back');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid credentials');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0A2540] via-[#0f172a] to-[#13294a] px-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="mx-auto h-20 w-20 rounded-2xl bg-white flex items-center justify-center mb-4 p-2 shadow-lg ring-1 ring-white/20">
            <img src="/assets/logo-white.png" alt="Elite Roofing and Construction" className="h-full w-full object-contain" />
          </div>
          <h1 className="text-3xl font-bold text-white tracking-tight" data-testid="admin-login-title">Elite Roofing</h1>
          <p className="mt-1 text-sm text-white/65 tracking-wide uppercase">Admin Portal</p>
        </div>
        <form onSubmit={onSubmit} className="rounded-2xl bg-white/[0.04] backdrop-blur-xl border border-white/10 p-7 sm:p-8 shadow-2xl">
          <h2 className="text-xl font-bold text-white mb-5">Sign in</h2>
          <div className="space-y-4">
            <div>
              <Label className="text-white/85 mb-1.5 block">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                <Input data-testid="admin-login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-2 focus-visible:ring-white/30" placeholder="you@elitercfl.com" autoComplete="email" />
              </div>
            </div>
            <div>
              <Label className="text-white/85 mb-1.5 block">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                <Input data-testid="admin-login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus-visible:ring-2 focus-visible:ring-white/30" placeholder="••••••••" autoComplete="current-password" />
              </div>
            </div>
          </div>
          <Button type="submit" disabled={submitting} className="mt-6 w-full h-11 bg-white text-[#0A2540] hover:bg-white/90 font-semibold" data-testid="admin-login-submit">
            {submitting ? 'Signing in…' : (<><LogIn className="h-4 w-4 mr-2" /> Sign in</>)}
          </Button>
        </form>
        <p className="text-center text-xs text-white/40 mt-6">Elite Roofing &amp; Construction LLC • Licensed CGC032777</p>
      </div>
    </div>
  );
}
